/**
 * Marco de todas las páginas con sesión: salto al contenido, encabezado con la navegación y el menú del
 * empleado, y el pie. Al cambiar de página el foco va al contenido nuevo.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, ScrollRestoration, useLocation } from "react-router";
import { useSesion } from "@/features/sesion";
import { t } from "@/shared/i18n";
import { Logo } from "@/shared/ui";
import estilos from "./PlantillaConsola.module.css";

const n = t.navegacion;

/** «Usuarios» queda marcada también en la ficha y en la invitación. */
const SECCIONES = [
  { ruta: "/", texto: n.usuarios, activa: (p: string) => p === "/" || p.startsWith("/usuarios") },
  { ruta: "/instituciones", texto: n.instituciones, activa: (p: string) => p.startsWith("/instituciones") },
  { ruta: "/auditoria", texto: n.auditoria, activa: (p: string) => p.startsWith("/auditoria") },
];

export function PlantillaConsola({ demo, children }: { readonly demo: boolean; readonly children: ReactNode }) {
  const principal = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const primera = useRef(true);

  useEffect(() => {
    if (primera.current) {
      primera.current = false;
      return;
    }
    // El lector de pantalla parte en el contenido nuevo; la página vuelve arriba sin saltar la barra.
    principal.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <>
      <ScrollRestoration />
      <a className={estilos.saltar} href="#contenido">
        {n.saltar}
      </a>
      {demo && (
        <div className={estilos.demo} role="note">
          {t.demo.aviso}
        </div>
      )}
      <header className={estilos.barra}>
        <div className={estilos.interior}>
          <Link className={estilos.marca} to="/">
            <Logo etiqueta={t.marca.nombre} />
            <span className={estilos.portal}>{t.marca.consola}</span>
          </Link>
          <nav className={estilos.navegacion} aria-label={n.etiqueta}>
            {SECCIONES.map((s) => (
              <Link
                key={s.ruta}
                to={s.ruta}
                className={estilos.enlace}
                aria-current={s.activa(pathname) ? "page" : undefined}
              >
                {s.texto}
              </Link>
            ))}
          </nav>
          <div className={estilos.lateral}>
            <MenuCuenta />
          </div>
        </div>
      </header>
      <main id="contenido" ref={principal} tabIndex={-1} className={estilos.principal}>
        <div className={estilos.contenido}>{children}</div>
      </main>
      <footer className={estilos.pie}>
        <div className={estilos.contenido}>
          <p>
            {t.pie.sitio} · {t.pie.horas}
          </p>
          <p>
            {t.pie.empresa} · {t.pie.por} <b>{t.pie.simplgo}</b> · {t.pie.derechos(new Date().getFullYear())}
          </p>
        </div>
      </footer>
    </>
  );
}

/** Nombre y correo del empleado; abre Salir. Se cierra con Escape o al hacer clic fuera. */
function MenuCuenta() {
  const { empleado, salir } = useSesion();
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAbierto(false);
        boton.current?.focus();
      }
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  return (
    <div className={estilos.cuenta} ref={caja}>
      <button
        ref={boton}
        type="button"
        className={estilos.quien}
        aria-expanded={abierto}
        aria-controls={id}
        onClick={() => {
          setAbierto(!abierto);
        }}
      >
        <b>{empleado?.nombre || empleado?.correo}</b>
        {empleado?.nombre && <span className={estilos.empresa}>{empleado.correo}</span>}
      </button>
      {abierto && (
        <ul id={id} className={estilos.menu} aria-label={n.menuCuenta}>
          <li>
            <button
              type="button"
              className={estilos.opcion}
              onClick={() => {
                setAbierto(false);
                void salir();
              }}
            >
              {n.salir}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
